import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import {
  createProjectsSlice,
  DEFAULT_PROJECT_COLUMN_ORDER,
  DEFAULT_ANALYTICS_COLUMN_ORDER,
} from "pages/media/projects/projectsSlice";
import { DEFAULT_VISIBLE_PROJECT_FIELD_COLUMNS } from "helpers/table/projectFieldColumns";
import { createAppSlice } from "components/appSlice";
import { createOnboardingSlice } from "pages/onboarding/onboardingSlice";
import { createClientPreferenceSlice } from "./clientPreferenceSlice";
import { CombinedSlice, CombinedVolatileSlice } from "./types";
import { createStoryboardSlice } from "pages/media/components/storyboard/storyboardSlice";
import { createProjectSlice } from "pages/media/projects/projectSlice";
import { PERSIST_STORE_KEY, SELECTED_ORG_ID_KEY } from "@/constants";
import { authService } from "@/services/authService";

// Storage adapter that gates writes on authentication. Reads still hydrate
// existing state, but setItem is a no-op when no user is logged in. This
// prevents the persist key from being created by:
//   - auth pages (login/register/etc.) that touch the store before login
//   - any post-logout set() that fires during the redirect/unmount cycle
const authGatedStorage = createJSONStorage(() => ({
  getItem: (name: string) => localStorage.getItem(name),
  setItem: (name: string, value: string) => {
    if (!authService.isAuthenticated()) return;
    localStorage.setItem(name, value);
  },
  removeItem: (name: string) => localStorage.removeItem(name),
}));

export const useBoundStore = create<CombinedSlice>()(
  devtools(
    persist(
      immer((...a) => ({
        ...createProjectsSlice(...a),
        ...createAppSlice(...a),
        ...createOnboardingSlice(...a),
        ...createClientPreferenceSlice(...a),
      })),
      {
        name: PERSIST_STORE_KEY,
        version: 4,
        storage: authGatedStorage,
        migrate: (persistedState: unknown, version: number) => {
          let state = persistedState as any;

          if (state && version < 1) {
            // v0 → v1: add layoutSettings
            state = {
              ...state,
              appState: {
                ...state.appState,
                layoutSettings: {
                  usedColors: [],
                },
              },
            };
          }

          if (state && version < 2) {
            // v1 → v2: add auth client state
            state = {
              ...state,
              selectedOrganizationId: localStorage.getItem(SELECTED_ORG_ID_KEY),
              sidebarCollapsed: false,
            };
          }

          if (state && version < 3) {
            // v2 → v3: add projects table column prefs
            state = {
              ...state,
              projectsState: {
                ...state.projectsState,
                columnOrder: DEFAULT_PROJECT_COLUMN_ORDER,
              },
            };
          }

          if (state && version < 4) {
            // v3 → v4: the single "analytics" group column is replaced by 4
            // individually-toggleable field columns (Views/Video reproductions/
            // Document interactions/Chat conversations), plus quick filters
            // (status/type) for the projects table.
            state = {
              ...state,
              projectsState: {
                ...state.projectsState,
                // Expand the old "analytics" slot into the 4 new keys at the
                // same position, preserving any custom drag order otherwise.
                columnOrder: (
                  state.projectsState?.columnOrder ??
                  DEFAULT_PROJECT_COLUMN_ORDER
                ).flatMap((key: string) =>
                  key === "analytics" ? DEFAULT_ANALYTICS_COLUMN_ORDER : [key]
                ),
                visibleFieldColumns: DEFAULT_VISIBLE_PROJECT_FIELD_COLUMNS,
                statusFilter: "all",
                typeFilter: null,
              },
            };
          }

          return state;
        },
      }
    )
  )
);

export const useVolatileBoundStore = create<CombinedVolatileSlice>()(
  devtools(
    immer((...a) => ({
      ...createStoryboardSlice(...a),
      ...createProjectSlice(...a),
    }))
  )
);
